import { History } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { formatDateTime, formatDurationBetween } from '~/utils/date';

import { useWorkflowHistory } from '../hooks/use-workflow-history';
import { useWorkflowHistoryStream } from '../hooks/use-workflow-history-stream';
import { useWorkflowDescribe, useWorkflowWorkers } from '../hooks/use-workflow-inspection';
import type { WorkflowFailure } from '../types/workflow-inspection.types';
import { isHistoryExpired } from '../utils/inspection-errors';

import { EventHistorySection } from './event-history/event-history-section';
import { InputResultPanels } from './input-result-panels';
import { SectionError } from './section-error';
import { SensitiveRevealToggle } from './sensitive-reveal-toggle';
import { CallStackTab } from './tabs/call-stack-tab';
import { MetadataTab } from './tabs/metadata-tab';
import { PendingActivitiesTab } from './tabs/pending-activities-tab';
import { QueriesTab } from './tabs/queries-tab';
import { RelationshipsTab } from './tabs/relationships-tab';
import { WorkersTab } from './tabs/workers-tab';

type InspectorTab =
  | 'history'
  | 'relationships'
  | 'pendingActivities'
  | 'workers'
  | 'callStack'
  | 'queries'
  | 'metadata';

export interface WorkflowInspectorProps {
  workflowId: string;
  /** Temporal run to inspect; `null` = the workflow's current run. */
  runId?: string | null;
  /** Persisted result/failure shown when Temporal detail has expired (retention). */
  fallbackResult?: unknown;
  fallbackFailure?: WorkflowFailure | null;
  /** Host relations shown at the top of the Relationships tab (e.g. retry lineage). */
  relationshipsExtra?: ReactNode;
  /** Render the "Reveal sensitive data" switch at the top. Hosts that render
   *  their own (e.g. `DeliveryRunInspector`, above its run header) pass false. */
  showRevealToggle?: boolean;
}

/**
 * Temporal-style inspector for any workflow execution — no delivery
 * knowledge, only a `workflowId` — so the delivery wizard, the deliveries
 * console and any future run screen share it. Execution summary, then tabs:
 * History (Input / Result + Event History, kept live by the history stream
 * while running), Relationships, Pending Activities, Workers, Call Stack,
 * Queries, Metadata. Tabs load lazily on open. All data is masked on arrival.
 */
export function WorkflowInspector({
  workflowId,
  runId = null,
  fallbackResult,
  fallbackFailure,
  relationshipsExtra,
  showRevealToggle = true,
}: WorkflowInspectorProps) {
  const { t } = useTranslation('workflow-inspection');
  // Local so "Open next run" (continued-as-new) can move the inspector along.
  const [activeRunId, setActiveRunId] = useState(runId);
  const [tab, setTab] = useState<InspectorTab>('history');

  const describe = useWorkflowDescribe(workflowId, activeRunId);
  // Fetched up front (small) so the Workers tab can show its count; the tab reuses the cache.
  const workers = useWorkflowWorkers(
    workflowId,
    describe.data?.runId ?? activeRunId,
    describe.isSuccess,
  );
  const expired = isHistoryExpired(describe.error);
  const history = useWorkflowHistory(workflowId, activeRunId, !expired);
  const isRunning = describe.data?.status === 'RUNNING';
  const stream = useWorkflowHistoryStream({
    workflowId,
    runId: activeRunId,
    enabled: isRunning && history.isSuccess,
  });

  if (describe.isLoading) {
    return (
      <div className="space-y-3" data-testid="workflow-inspector-loading">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const revealToggle = showRevealToggle ? (
    <div className="flex justify-end">
      <SensitiveRevealToggle />
    </div>
  ) : null;

  if (expired) {
    return (
      <div className="space-y-4" data-testid="workflow-inspector-expired">
        {revealToggle}
        <div className="bg-muted/40 flex items-start gap-3 rounded-lg border p-4">
          <History aria-hidden="true" className="text-muted-foreground mt-0.5 size-5 shrink-0" />
          <div>
            <p className="text-foreground text-sm font-semibold">{t('history.expiredTitle')}</p>
            <p className="text-muted-foreground mt-0.5 text-sm">{t('history.expiredBody')}</p>
          </div>
        </div>
        {(fallbackResult !== undefined || fallbackFailure) && (
          <InputResultPanels
            input={undefined}
            result={fallbackResult}
            failure={fallbackFailure}
            isRunning={false}
            fromRunRecord
          />
        )}
      </div>
    );
  }

  if (describe.isError || !describe.data) {
    return (
      <SectionError
        onRetry={() => void describe.refetch()}
        isRetrying={describe.isFetching}
        testId="workflow-inspector-error"
      />
    );
  }

  const detail = describe.data;
  // `copy` marks ids people paste elsewhere (support tickets, Temporal search):
  // shown on one truncated line with the full value in a tooltip + copy button.
  const summary: { label: string; value: string | null; copy?: boolean }[] = [
    { label: t('summary.workflowType'), value: detail.workflowType },
    { label: t('summary.taskQueue'), value: detail.taskQueue },
    {
      label: t('summary.started'),
      value: detail.startTime ? formatDateTime(detail.startTime) : null,
    },
    {
      label: t('summary.closed'),
      value: detail.closeTime ? formatDateTime(detail.closeTime) : null,
    },
    {
      label: t('summary.duration'),
      value:
        detail.startTime && detail.closeTime
          ? formatDurationBetween(detail.startTime, detail.closeTime)
          : null,
    },
    {
      label: t('summary.historyLength'),
      value: detail.historyLength !== null ? String(detail.historyLength) : null,
    },
    { label: t('summary.temporalRun'), value: detail.runId, copy: true },
  ];

  return (
    <div className="space-y-6" data-testid="workflow-inspector">
      {revealToggle}
      <dl
        data-testid="workflow-inspector-summary"
        className="bg-background grid grid-cols-2 gap-x-6 gap-y-3 rounded-xl border p-4 text-sm md:grid-cols-4 xl:grid-cols-7"
      >
        {summary
          .filter((s) => s.value)
          .map((s) => (
            <div key={s.label} className="min-w-0">
              <dt className="text-muted-foreground text-xs">{s.label}</dt>
              <dd className="text-foreground flex items-center gap-1 font-medium">
                {s.copy ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="min-w-0 cursor-default truncate">{s.value}</span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-none font-mono">{s.value}</TooltipContent>
                  </Tooltip>
                ) : (
                  <span className="min-w-0 break-all">{s.value}</span>
                )}
                {s.copy && (
                  <CopyIconButton
                    text={s.value}
                    label={t('summary.copyValue', { label: s.label })}
                  />
                )}
              </dd>
            </div>
          ))}
      </dl>

      {detail.continuedAsNewRunId && (
        <div className="bg-info-50 flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-2.5 text-sm">
          <span>{t('summary.continuedAsNew')}</span>
          <Button
            type="button"
            variant="link"
            className="h-auto cursor-pointer p-0"
            data-testid="workflow-inspector-open-next-run-button"
            onClick={() => {
              setActiveRunId(detail.continuedAsNewRunId);
            }}
          >
            {t('summary.openNextRun')}
          </Button>
        </div>
      )}

      {/* Sized to content (no `sm:max-w-md` cap) so every tab label shows in
          full; the wrapper scrolls on viewports too narrow for all seven. */}
      <div className="overflow-x-auto">
        <SegmentedTabs
          value={tab}
          onChange={setTab}
          ariaLabel={t('tabs.ariaLabel')}
          testId="workflow-inspector-tabs"
          className="bg-background w-max max-w-none border sm:max-w-none"
          options={[
            { value: 'history', label: withCount(t('tabs.history'), history.data?.events.length) },
            { value: 'relationships', label: t('tabs.relationships') },
            {
              value: 'pendingActivities',
              label: withCount(t('tabs.pendingActivities'), detail.pendingActivities.length),
            },
            { value: 'workers', label: withCount(t('tabs.workers'), workers.data?.workers.length) },
            { value: 'callStack', label: t('tabs.callStack') },
            { value: 'queries', label: t('tabs.queries') },
            { value: 'metadata', label: t('tabs.metadata') },
          ]}
        />
      </div>

      {tab === 'history' && (
        <div className="space-y-6">
          <InputResultPanels
            input={detail.input}
            result={detail.result}
            failure={detail.failure}
            isRunning={isRunning}
          />
          {history.isLoading ? (
            <div className="space-y-2" data-testid="workflow-inspector-history-loading">
              <p className="text-muted-foreground text-sm">{t('history.loading')}</p>
              <Skeleton className="h-64 w-full" />
            </div>
          ) : history.isError || !history.data ? (
            <SectionError
              onRetry={() => void history.refetch()}
              isRetrying={history.isFetching}
              testId="workflow-inspector-history-error"
            />
          ) : (
            <>
              {history.data.capped && (
                <p className="bg-warning/10 rounded-md px-3 py-2 text-xs">
                  {t('history.capped', { count: history.data.events.length })}
                </p>
              )}
              <EventHistorySection
                workflowId={workflowId}
                runId={detail.runId}
                events={history.data.events}
                streamStatus={stream.status}
                frozen={stream.frozen}
                onFrozenChange={stream.setFrozen}
                bufferedCount={stream.bufferedCount}
              />
            </>
          )}
        </div>
      )}
      {tab === 'relationships' && (
        <RelationshipsTab
          detail={detail}
          extra={relationshipsExtra}
          renderChildInspector={(childId, childRunId) => (
            <WorkflowInspector workflowId={childId} runId={childRunId} />
          )}
        />
      )}
      {tab === 'pendingActivities' && <PendingActivitiesTab detail={detail} />}
      {tab === 'workers' && <WorkersTab workflowId={workflowId} runId={detail.runId} />}
      {tab === 'callStack' && (
        <CallStackTab workflowId={workflowId} runId={detail.runId} isRunning={isRunning} />
      )}
      {tab === 'queries' && <QueriesTab workflowId={workflowId} runId={detail.runId} />}
      {tab === 'metadata' && <MetadataTab detail={detail} />}
    </div>
  );
}

/** `History (34)` — count omitted until known. */
function withCount(label: string, count: number | undefined): string {
  return count === undefined ? label : `${label} (${String(count)})`;
}
